import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatIntegerNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import { capitalize } from "@aprao/common/src/string/capitalize.js";
import type { OperatedAssetGroupFacade } from "@aprao/model/src/stores/revenue/OperatedAssetGroupFacade.js";
import type { SalesTaxConfigurationFacade } from "@aprao/model/src/stores/SalesTaxConfigurationFacade.js";
import type { TaxFacade } from "@aprao/model/src/stores/TaxFacade.js";
import { observer } from "mobx-react-lite";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../../common/ApraoI18n.js";
import { useNumberUnits } from "../../../common/useNumberUnits.js";
import { buildRevenueItemDescription } from "../../../i18n/buildRevenueItemDescription.js";
import { buildSalesTaxName } from "../../../i18n/buildSalesTaxName.js";
import { periodGranularityLabels } from "../../../i18n/periodGranularityLabels.js";
import { SimpleTableRow, SimpleTableSection } from "../../SimpleTableRow.js";

const WEEK_COUNT_IN_YEAR = 52;

export const OperatedAssetTableSection = observer(
  // eslint-disable-next-line max-lines-per-function
  function OperatedAssetTableSection(props: {
    label: string;
    revenueGroup: OperatedAssetGroupFacade;
    salesTaxConfiguration: SalesTaxConfigurationFacade | null;
    tax: TaxFacade;
  }) {
    const { label, revenueGroup, tax, salesTaxConfiguration } = props;

    const { msg, effectiveLocale } = useI18n();
    const { lengthUnit, currency } = useNumberUnits();

    return (
      <>
        <SimpleTableSection hasStartGutter>
          <SimpleTableRow
            isHeader
            cells={[
              label,
              null,
              `${capitalize(
                periodGranularityLabels[props.revenueGroup.periodGranularity],
              )} rent (${currency.symbol})`,
              "Weeks let",
              `${lengthUnit.symbol}² (${msg("global.nia")})`,
              `Annual rent (${currency.symbol})`,
            ]}
          />
          {revenueGroup.items.map((item, itemIdx) => (
            <SimpleTableRow
              key={itemIdx}
              cells={[
                buildRevenueItemDescription(effectiveLocale, item.$model),
                null,
                mapForNullable(
                  item.periodRent.relativesSet.getRelative("absolute"),
                  formatDecimalNumber,
                ),
                formatIntegerNumber(
                  effectiveLocale,
                  item.weekLetCount ?? WEEK_COUNT_IN_YEAR,
                ),
                mapForNullable(item.aggregateArea, formatDecimalNumber),
                mapForNullable(
                  item.aggregateAnnualRentalIncome,
                  formatDecimalNumber,
                ),
              ]}
            />
          ))}
          <SimpleTableRow
            bold
            borderBottom
            borderTop
            cells={[
              "Gross annual income",
              null,
              null,
              null,
              formatDecimalNumber(revenueGroup.areaTotal ?? FRACTION_ZERO),
              formatDecimalNumber(
                revenueGroup.annualRentalIncomeTotal ?? FRACTION_ZERO,
              ),
            ]}
          />
        </SimpleTableSection>
        <SimpleTableSection>
          <SimpleTableRow
            isHeader
            cells={[
              "Operating costs",
              null,
              null,
              "% Income",
              `${currency.symbol}/${lengthUnit.symbol}²`,
              `Total (${currency.symbol})`,
            ]}
          />
          {revenueGroup.operatingCosts.map((cost, costIdx) => (
            <SimpleTableRow
              key={costIdx}
              cells={[
                cost.named.name,
                null,
                null,
                formatPercentage(
                  cost.relativesSet.getRelative("ratioAnnualRentalIncome") ??
                    FRACTION_ZERO,
                ),
                formatDecimalNumber(
                  cost.relativesSet.getRelative("ratioArea") ?? FRACTION_ZERO,
                ),
                formatDecimalNumber(
                  cost.relativesSet.getRelative("absolute") ?? FRACTION_ZERO,
                ),
              ]}
            />
          ))}
          <SimpleTableRow
            bold
            borderBottom
            borderTop
            thickerBorder
            cells={[
              "Total operating costs",
              null,
              null,
              null,
              null,
              formatDecimalNumber(
                revenueGroup.operatingCostsTotal ?? FRACTION_ZERO,
              ),
            ]}
          />
        </SimpleTableSection>
        <SimpleTableSection>
          <SimpleTableRow
            bold
            borderBottom
            borderTop
            thickerBorder
            cells={[
              "Net annual income",
              null,
              null,
              null,
              null,
              formatDecimalNumber(revenueGroup.annualIncome ?? FRACTION_ZERO),
            ]}
          />
          <SimpleTableRow
            cells={[
              null,
              null,
              null,
              revenueGroup.rentFreeMonthsCount != null && "Rent free (months)",
              "Yield",
              `Total (${currency.symbol})`,
            ]}
          />
          <SimpleTableRow
            bold
            borderTop
            borderBottom
            thickerBorder
            cells={[
              "Gross value",
              null,
              null,
              mapForNullable(revenueGroup.rentFreeMonthsCount, (count) =>
                formatIntegerNumber(effectiveLocale, count),
              ),
              formatPercentage(revenueGroup.yield_ ?? FRACTION_ZERO),
              formatDecimalNumber(revenueGroup.valueGross ?? FRACTION_ZERO),
            ]}
          />
          <SimpleTableRow
            cells={[
              "Costs",
              null,
              formatPercentage(revenueGroup.costsOnValue ?? FRACTION_ZERO),
              null,
              null,
              formatDecimalNumber(revenueGroup.costsTotal ?? FRACTION_ZERO),
            ]}
          />
          {salesTaxConfiguration && (
            <SimpleTableRow
              cells={[
                buildSalesTaxName(salesTaxConfiguration.$model),
                null,
                formatPercentage(tax.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO),
                null,
                null,
                formatDecimalNumber(
                  tax.getTaxAmountByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
                ),
              ]}
            />
          )}
          <SimpleTableRow
            bold
            borderBottom
            borderTop
            thickerBorder
            cells={[
              "Net value",
              null,
              null,
              null,
              null,
              formatDecimalNumber(
                tax.getValueNetByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
              ),
            ]}
          />
        </SimpleTableSection>
      </>
    );
  },
);
